import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Button, IconButton, Text, TextInput, useTheme } from '@/components/ui';
import type { ColumnPreset } from '@/hooks/use-column-presets';
import { spacing } from '@/styles/theme';
import { Alert } from '@/utils/alert';

interface ColumnPresetBarProps {
	presets: ColumnPreset[];
	onApply: (preset: ColumnPreset) => void;
	onSave: (name: string) => void;
	onDelete: (preset: ColumnPreset) => void;
}

export const ColumnPresetBar: React.FC<ColumnPresetBarProps> = ({
	presets, onApply, onSave, onDelete,
}) => {
	const theme = useTheme();
	const [composing, setComposing] = useState(false);
	const [name, setName] = useState('');

	const openComposer = () => {
		setName('');
		setComposing(true);
	};

	const handleSave = () => {
		const trimmed = name.trim();
		if (!trimmed) return;
		onSave(trimmed);
		setComposing(false);
	};

	const handleDelete = (preset: ColumnPreset) => {
		Alert.alert(
			'Delete preset',
			`Remove the “${preset.name}” preset? Saved columns aren’t affected.`,
			[
				{ text: 'Cancel', style: 'cancel' },
				{ text: 'Delete', style: 'destructive', onPress: () => onDelete(preset) },
			],
		);
	};

	return (
		<View style={styles.bar} testID="column-picker-preset-bar">
			<Text style={[styles.label, { color: theme.colors.onSurfaceVariant }]}>
				PRESETS
			</Text>
			<View style={styles.chips}>
				{presets.map((p) => (
					<View
						key={p.id}
						style={[
							styles.chip,
							{ borderColor: theme.colors.outline, backgroundColor: theme.colors.surface },
						]}
					>
						<Pressable
							onPress={() => onApply(p)}
							style={styles.chipLabelPress}
							accessibilityRole="button"
							accessibilityLabel={`Apply preset ${p.name}`}
							testID={`column-picker-preset-${p.id}`}
						>
							<Text style={[styles.chipLabel, { color: theme.colors.onSurface }]} numberOfLines={1}>
								{p.name}
							</Text>
						</Pressable>
						<Pressable
							onPress={() => handleDelete(p)}
							style={styles.chipClose}
							accessibilityRole="button"
							accessibilityLabel={`Delete preset ${p.name}`}
							testID={`column-picker-preset-${p.id}-delete`}
							hitSlop={6}
						>
							<Ionicons name="close" size={14} color={theme.colors.onSurfaceVariant} />
						</Pressable>
					</View>
				))}

				{composing ? (
					<View style={styles.composer}>
						<TextInput
							value={name}
							onChangeText={setName}
							placeholder="Preset name…"
							style={styles.composerInput}
							onSubmitEditing={handleSave}
							autoFocus
							testID="column-picker-preset-name-input"
						/>
						<Button
							mode="contained"
							onPress={handleSave}
							disabled={!name.trim()}
							testID="column-picker-preset-save"
						>
							Save
						</Button>
						<IconButton
							icon="close"
							size={18}
							onPress={() => setComposing(false)}
							accessibilityLabel="Cancel preset save"
						/>
					</View>
				) : (
					<Pressable
						onPress={openComposer}
						style={[
							styles.chip,
							styles.chipAdd,
							{ borderColor: theme.colors.primary },
						]}
						accessibilityRole="button"
						accessibilityLabel="Save current columns as preset"
						testID="column-picker-preset-add"
					>
						<Ionicons name="add" size={14} color={theme.colors.primary} />
						<Text style={[styles.chipAddLabel, { color: theme.colors.primary }]}>
							Save current
						</Text>
					</Pressable>
				)}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	bar: {
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: spacing.xs,
		marginBottom: spacing.m,
	},
	label: {
		fontSize: 11,
		fontWeight: '700',
		letterSpacing: 0.6,
		marginRight: spacing.xs,
	},
	chips: {
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: spacing.xs,
		flex: 1,
	},
	chip: {
		flexDirection: 'row',
		alignItems: 'center',
		borderWidth: 1,
		borderRadius: 16,
		paddingLeft: 12,
		paddingRight: 4,
		paddingVertical: 2,
		gap: 4,
		maxWidth: 220,
	},
	chipLabelPress: {
		paddingVertical: 4,
	},
	chipLabel: {
		fontSize: 13,
		fontWeight: '500',
	},
	chipClose: {
		padding: 4,
		borderRadius: 999,
		alignItems: 'center',
		justifyContent: 'center',
	},
	chipAdd: {
		paddingRight: 12,
		paddingVertical: 4,
		borderStyle: 'dashed',
		gap: 2,
	},
	chipAddLabel: {
		fontSize: 13,
		fontWeight: '600',
	},
	composer: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: spacing.xs,
		flexGrow: 1,
		flexShrink: 1,
		minWidth: 220,
	},
	composerInput: {
		flex: 1,
		minWidth: 120,
	},
});
